import { ActionIcon, Anchor, Box, FileButton, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconFileTypePdf, IconTrash, IconUpload } from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import {
  useDeleteTermsAndConditionsPDF,
  useUploadTermsAndConditionsPDF,
} from 'modules/Settings/Configuration/api';
import classes from './TermsAndConditionsDocument.module.css';

type Props = {
  termsAndConditionsId: number | undefined;
  hasPDF: boolean;
  pdfFilename: string;
  pdfUrl: string;
};

// The document is its own resource: picking a file uploads it straight away
// rather than waiting for the card's Save. A set that has never been saved has
// no id to attach a document to, so uploading waits until it has one.
export const TermsAndConditionsDocument = ({
  termsAndConditionsId,
  hasPDF,
  pdfFilename,
  pdfUrl,
}: Props) => {
  const { t } = useTranslation();
  const { uploadTermsAndConditionsPDF, isUploadTermsAndConditionsPDFLoading } =
    useUploadTermsAndConditionsPDF();
  const { deleteTermsAndConditionsPDF, isDeleteTermsAndConditionsPDFLoading } =
    useDeleteTermsAndConditionsPDF();

  const isPending = isUploadTermsAndConditionsPDFLoading || isDeleteTermsAndConditionsPDFLoading;
  const isUploadable = termsAndConditionsId != null;

  const handlePick = (file: File | null) => {
    if (file == null || termsAndConditionsId == null) return;
    uploadTermsAndConditionsPDF({ termsAndConditionsId, pdf: file });
  };

  if (!hasPDF) {
    return (
      <Box className={classes.container}>
        <FileButton onChange={handlePick} accept="application/pdf" disabled={!isUploadable}>
          {props => (
            <Box {...props} className={classes.dropzone} data-disabled={!isUploadable || undefined}>
              <Stack gap={4} align="center" className={classes.dropzoneContent}>
                <IconUpload size={20} stroke={1.8} />
                <Text size="sm" fw={600} c="primary">
                  {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.UPLOAD_PDF)}
                </Text>
                <Text size="xs" c="dimmed">
                  {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.UPLOAD_PDF_HINT)}
                </Text>
              </Stack>
            </Box>
          )}
        </FileButton>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.document}>
        <Stack gap="xs" align="center" w="100%" h="100%" className={classes.documentLayout}>
          {/* Auto margins centre the icon and name in whatever space the actions
              leave, so the actions themselves stay pinned to the bottom edge. */}
          <Stack gap="xs" align="center" maw="100%" my="auto" className={classes.documentSummary}>
            <ThemeIcon variant="light" size={56} radius="md" className={classes.pdfIcon}>
              <IconFileTypePdf size={32} stroke={1.6} />
            </ThemeIcon>
            <Text size="xs" fw={500} ta="center" className={classes.filename} title={pdfFilename}>
              {pdfFilename}
            </Text>
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <Anchor href={pdfUrl} target="_blank" rel="noreferrer" size="xs" fw={600}>
              {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.VIEW_PDF)}
            </Anchor>
            <FileButton onChange={handlePick} accept="application/pdf">
              {props => (
                <Anchor {...props} component="button" type="button" size="xs" fw={600} c="dimmed">
                  {t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.REPLACE_PDF)}
                </Anchor>
              )}
            </FileButton>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              loading={isPending}
              aria-label={t(i18nKeys.SETTINGS.PREFERENCES.TERMS_AND_CONDITIONS.REMOVE_PDF)}
              onClick={() =>
                termsAndConditionsId != null &&
                deleteTermsAndConditionsPDF({ termsAndConditionsId })
              }
            >
              <IconTrash size={14} stroke={1.8} />
            </ActionIcon>
          </Group>
        </Stack>
      </Box>
    </Box>
  );
};
